import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';

export interface FieldValue {
  value: string;
  label: string;
}

export interface Field {
  title: string;
  name?: string; // Made optional temporarily to avoid breaking existing fields
  description: string;
  type: 'radio-button' | 'dropdown' | 'date' | 'time' | 'number' | 'textarea' | 'checkbox-group' | 'calculated' | 'compound' | 'signature' | 'text' | 'table'|'title'|'fixed-rows-table';
  values?: FieldValue[];
  required: boolean;
  dependencyFieldName?: string;
  hasDependency?: boolean;
  unit?: string;
  fields?: Field[]; // For compound fields
  columns?: TableColumn[]; // For table fields
  predefinedRows?: any[]; // For table fields with predefined rows
}

export interface TableColumn {
  title: string;
  type: 'text' | 'date' | 'dropdown' | 'number' | 'textarea' | 'checkbox' | 'statement';
  required: boolean;
  values?: FieldValue[];
}

export interface Form {
  name: string;
  route: string;
  title?: string;
  description?: string;
  fields: Field[];
}

export interface Folder {
  name: string;
  icon: string;
  color: string;
  description: string;
  title: string;
  subTitle: string;
  forms: Form[];
}

export interface FoldersData {
  folders: Folder[];
}

// Define the data as a properly typed constant
const foldersData: FoldersData = {
  "folders": [
    {
      "name": "Schedule of Activities",
      "icon": "fa fa-calendar",
      "color": "#2196F3",
      "description": "Schedule and timeline management for study activities",
      "title": "Schedule of Activities",
      "subTitle": "Study timeline and activity management",
      "forms": [
        {
          "name": "SCHEDULE-ACTIVITIES",
          "route": "Schedule-Activities",
          "title": "Schedule-Activities",
          "description": "Schedule of activities for each visit",
          "fields": [
            {
              "title": "Visit Activities Schedule",
              "name": "visit_activities_schedule",
              "type": "table",
              "required": true,
              "description": "Schedule of activities for each visit with predefined visit names and activity columns",
              "columns": [
                {
                  "title": "Activity",
                  "type": "text",
                  "required": true
                },
                {
                  "title": "BMI Check",
                  "type": "dropdown",
                  "required": false,
                  "values": [
                    {"value": "Complete", "label": "Complete"},
                    {"value": "Incomplete", "label": "Incomplete"},
                    {"value": "Locked", "label": "Locked"},
                    {"value": "Not Applicable", "label": "Not Applicable"}
                  ]
                },
                {
                  "title": "Clinical Exam",
                  "type": "dropdown",
                  "required": false,
                  "values": [
                    {"value": "Complete", "label": "Complete"},
                    {"value": "Incomplete", "label": "Incomplete"},
                    {"value": "Locked", "label": "Locked"},
                    {"value": "Not Applicable", "label": "Not Applicable"}
                  ]
                },
                {
                  "title": "HbA1c Test",
                  "type": "dropdown",
                  "required": false,
                  "values": [
                    {"value": "Complete", "label": "Complete"},
                    {"value": "Incomplete", "label": "Incomplete"},
                    {"value": "Locked", "label": "Locked"},
                    {"value": "Not Applicable", "label": "Not Applicable"}
                  ]
                },
                {
                  "title": "Product Retrieval",
                  "type": "dropdown",
                  "required": false,
                  "values": [
                    {"value": "Complete", "label": "Complete"},
                    {"value": "Incomplete", "label": "Incomplete"},
                    {"value": "Locked", "label": "Locked"},
                    {"value": "Not Applicable", "label": "Not Applicable"}
                  ]
                },
                {
                  "title": "Product Dispensing",
                  "type": "dropdown",
                  "required": false,
                  "values": [
                    {"value": "Complete", "label": "Complete"},
                    {"value": "Incomplete", "label": "Incomplete"},
                    {"value": "Locked", "label": "Locked"},
                    {"value": "Not Applicable", "label": "Not Applicable"}
                  ]
                },
                {
                  "title": "Discontinuation",
                  "type": "checkbox",
                  "required": false
                },
                {
                  "title": "Discontinuation Comment",
                  "type": "text",
                  "required": false
                }
              ],
              "predefinedRows": [
                {
                  "visitName": "Screening Visit",
                  "isDiscontinued": false,
                  "BMI Check": "",
                  "Clinical Exam": "",
                  "HbA1c Test": "",
                  "Product Retrieval": "",
                  "Product Dispensing": "",
                  "discontinuationComment": ""
                },
                {
                  "visitName": "Baseline Visit",
                  "isDiscontinued": false,
                  "BMI Check": "",
                  "Clinical Exam": "",
                  "HbA1c Test": "",
                  "Product Retrieval": "",
                  "Product Dispensing": "",
                  "discontinuationComment": ""
                },
                {
                  "visitName": "Follow up Visit 01",
                  "isDiscontinued": false,
                  "BMI Check": "",
                  "Clinical Exam": "",
                  "HbA1c Test": "",
                  "Product Retrieval": "",
                  "Product Dispensing": "",
                  "discontinuationComment": ""
                },
                {
                  "visitName": "Follow up Visit 02",
                  "isDiscontinued": false,
                  "BMI Check": "",
                  "Clinical Exam": "",
                  "HbA1c Test": "",
                  "Product Retrieval": "",
                  "Product Dispensing": "",
                  "discontinuationComment": ""
                },
                {
                  "visitName": "Study Completion",
                  "isDiscontinued": false,
                  "BMI Check": "",
                  "Clinical Exam": "",
                  "HbA1c Test": "",
                  "Product Retrieval": "",
                  "Product Dispensing": "",
                  "discontinuationComment": ""
                }
              ]
            }
          ]
        }
      ]
    },
    {
      "name": "Screening Visit",
      "icon": "fa fa-check-square-o",
      "color": "#4CAF50",
      "description": "Initial assessment and data collection for the study",
      "title": "Screening Visit",
      "subTitle": "Initial assessment and data collection",
      "forms": [
        {
          "name": "INFORMED CONSENT",
          "route": "informed-consent",
          "fields": [
            {
              "title": "Did the Subject read, understand, and sign the Informed Consent form",
              "name": "informed_consent_completion",
              "description": "Confirmation of informed consent completion",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            },
            {
              "title": "Language of Informed Consent Form given to the patient",
              "name": "informed_consent_language",
              "description": "Language used for informed consent documentation",
              "type": "dropdown" as const,
              "values": [
                {
                  "value": "Malayalam",
                  "label": "Malayalam"
                },
                {
                  "value": "English",
                  "label": "English"
                },
                {
                  "value": "Hindi",
                  "label": "Hindi"
                }
              ],
              "required": true
            },
            {
              "title": "Date of Signing",
              "name": "date_of_signing",
              "description": "Date when the informed consent was signed",
              "type": "date" as const,
              "required": true
            },
            {
              "title": "Time of Signing",
              "description": "Time when the informed consent was signed",
              "type": "time" as const,
              "required": true
            }
          ]
        },
        {
          "name": "SCREENING VISIT DATE",
          "route": "Screening Visit Date",
          "fields": [
            {
              "title": "Date of Screening Visit",
              "description": "The date when the screening visit took place",
              "type": "date" as const,
              "required": true
            }
          ]
        },
        {
          "name": "DEMOGRAPHY",
          "route": "demography",
          "fields": [
            {
              "title": "Gender",
              "description": "Subject's gender identification",
              "type": "dropdown" as const,
              "values": [
                {
                  "value": "Male",
                  "label": "Male"
                },
                {
                  "value": "Female",
                  "label": "Female"
                },
                {
                  "value": "Other",
                  "label": "Other"
                }
              ],
              "required": true
            },
            {
              "title": "Date of Birth (DD/MMM/YYYY)",
              "description": "Subject's date of birth",
              "type": "date" as const,
              "required": true
            },
            {
              "title": "Age (Years & Months)",
              "description": "Calculated age based on date of birth",
              "type": "calculated" as const,
              "required": false
            }
          ]
        },
        {
          "name": "ANTHROPOMETRIC DATA",
          "route": "anthropometric-data",
          "fields": [
            {
              "title": "Weight (kg)",
              "description": "Subject's weight in kilograms",
              "type": "number" as const,
              "unit": "kg",
              "required": true
            },
            {
              "title": "Height (m)",
              "description": "Subject's height in meters",
              "type": "number" as const,
              "unit": "m",
              "required": true
            },
            {
              "title": "Waist Circumference (cm) (Part of IDRS Assessment)",
              "description": "Subject's waist circumference in centimeters",
              "type": "number" as const,
              "unit": "cm",
              "required": true
            },
            {
              "title": "Body Mass Index (BMI)",
              "description": "Calculated Body Mass Index",
              "type": "calculated" as const,
              "required": false
            }
          ]
        },
        {
          "name": "MEDICAL HISTORY",
          "route": "medical-history",
          "fields": [
            {
              "title": "Does the subject have any relevant medical history",
              "description": "Confirmation of medical history",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            }
          ]
        },
        {
          "name": "PRIOR MEDICATION",
          "route": "prior-medication",
          "fields": [
            {
              "title": "Any relevant medications taken by subject within the last 1 month up to screening visit",
              "description": "Confirmation of prior medication",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            }
          ]
        },
        {
          "name": "VITAL SIGNS",
          "route": "vital-signs",
          "fields": [
            {
              "title": "Was vital signs evaluation performed",
              "description": "Confirmation of vital signs evaluation",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            }
          ]
        },
        {
          "name": "PHYSICAL AND SYSTEMIC EXAMINATION",
          "route": "physical-exam",
          "fields": [
            {
              "title": "Was physical and systemic examination performed",
              "description": "Confirmation of physical examination",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            }
          ]
        },
        {
          "name": "PREGNANCY DECLARATION",
          "route": "pregnancy-declaration",
          "fields": [
            {
              "title": "Date of Last Menstrual Period (LMP)",
              "description": "Date of last menstrual period",
              "type": "date" as const,
              "required": true
            },
            {
              "title": "Was Urine Pregnancy Test (UPT) done",
              "description": "Whether urine pregnancy test was performed",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            },
            {
              "title": "UPT Result",
              "description": "Result of urine pregnancy test",
              "type": "radio-button" as const,
              "dependencyFieldName": "was_urine_pregnancy_test_(upt)_done",
              "hasDependency": true,
              "values": [
                {
                  "value": "Positive",
                  "label": "Positive"
                },
                {
                  "value": "Negative",
                  "label": "Negative"
                }
              ],
              "required": true
            }
          ]
        },
        {
          "name": "IDRS - INDIAN DIABETES RISK SCORE ASSESSMENT",
          "route": "idrs",
          "fields": [
            {
              "title": "Was IDRS Assessment performed",
              "description": "Confirmation of IDRS assessment",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            }
          ]
        },
        {
          "name": "LABORATORY INVESTIGATION",
          "route": "laboratory-investigation",
          "fields": [
            {
              "title": "Was Laboratory Investigation performed",
              "description": "Confirmation of laboratory investigation",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            },
            {
              "title": "Date of Sample Collection",
              "description": "Date when samples were collected",
              "type": "date" as const,
              "required": false
            },
            {
              "title": "Time of Sample Collection",
              "description": "Time when samples were collected",
              "type": "time" as const,
              "required": false
            }
          ]
        },
        {
          "name": "ASSESSMENT OF INCLUSION AND EXCLUSION CRITERIA",
          "route": "inclusion-exclusion",
          "title": "Inclusion Criteria",
          "description": "Assessment of inclusion criteria for study participation",
          "fields": [
            {
              "title": "Inclusion Criteria",
              "description": "Assessment of inclusion criteria for study participation",
              "type": "title" as const,
              "required": false
            },
            {
              "title": "Age is between 35 years and 60 years (both ages included)",
              "description": "Age inclusion criteria check",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            },
            {
              "title": "Moderate or High-Risk category according to Indian Diabetes Risk Score",
              "description": "Risk category inclusion criteria check",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            },
            {
              "title": "HbA1c value in the range 5.7% - 6.4%",
              "description": "HbA1c inclusion criteria check",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            },
            {
              "title": "BMI indicating Overweight or Obese",
              "description": "BMI inclusion criteria check",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            },
            {
              "title": "The subject is capable to give informed consent",
              "description": "Informed consent capability check",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            },
            {
              "title": "Exclusion Criteria",
              "description": "Assessment of exclusion criteria for study participation",
              "type": "title" as const,
              "required": false
            },
            // {
            //   "type": "title" ,
            //   "title": "Exclusion Criteria",
            //   "description": "Assessment of exclusion criteria for study participation",
            //   "line": true
            // },
            {
              "title": "History of severe disease (Hypertension, renal disease, etc.)",
              "description": "Severe disease exclusion criteria check",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            },
            {
              "title": "Pregnant or lactating woman",
              "description": "Pregnancy exclusion criteria check",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            },
            {
              "title": "Any medication that may impact the study",
              "description": "Medication impact exclusion criteria check",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            }
          ]
        },
        {
          "name": "ELIGIBILITY ASSESSMENT",
          "route": "eligibility-assessment",
          "fields": [
            {
              "title": "Has the subject enrolled in the study",
              "description": "Whether subject is enrolled in the study",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            }
          ]
        },
        {
          "name": "END OF SCREENING VISIT",
          "route": "end-of-screening",
          "fields": [
            {
              "title": "Principal Investigator's Signature",
              "description": "Principal investigator signature",
              "type": "signature" as const,
              "required": true
            },
            {
              "title": "Date",
              "description": "Date of screening completion",
              "type": "date" as const,
              "required": true
            }
          ]
        }
      ]
    },
    {
      "name": "Baseline Visit",
      "icon": "fa fa-clipboard",
      "color": "#FF9800",
      "description": "Baseline measurements and initial study procedures",
      "title": "Baseline Visit",
      "subTitle": "Baseline measurements and procedures",
      "forms": [
        {
          "name": "BASELINE RECORD",
          "route": "baseline-record",
          "fields": [
            {
              "title": "Date of the Baseline Visit",
              "description": "Date when the baseline visit took place",
              "type": "date" as const,
              "required": true
            },
            {
              "title": "Was the subject recruited",
              "description": "Confirmation of subject recruitment",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            }
          ]
        },
        {
          "name": "PRAKRITI ANALYSIS",
          "route": "baseline-prakriti-analysis",
          "fields": [
            {
              "title": "Was the prakriti analysis done for the subject",
              "description": "Confirmation of prakriti analysis completion",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            },
            {
              "title": "Prakriti Type",
              "description": "Type of prakriti identified",
              "type": "dropdown" as const,
              "values": [
                {
                  "value": "vata",
                  "label": "Vata"
                },
                {
                  "value": "pitta",
                  "label": "Pitta"
                },
                {
                  "value": "kapha",
                  "label": "Kapha"
                },
                {
                  "value": "vata_pitta",
                  "label": "Vata-Pitta"
                },
                {
                  "value": "vata_kapha",
                  "label": "Vata-Kapha"
                },
                {
                  "value": "pitta_kapha",
                  "label": "Pitta-Kapha"
                },
                {
                  "value": "kapha_pitta",
                  "label": "Kapha-Pitta"
                }
              ],
              "required": false
            },
            {
              "title": "Comments",
              "description": "Additional comments on prakriti analysis",
              "type": "textarea" as const,
              "required": false
            }
          ]
        },
        {
          "name": "VITAL SIGNS",
          "route": "baseline-vital-signs",
          "fields": [
            {
              "title": "Was Vital Signs evaluation performed",
              "description": "Confirmation of vital signs evaluation",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            }
          ]
        },
        {
          "name": "PHYSICAL AND SYSTEMIC EXAMINATION",
          "route": "baseline-physical-exam",
          "fields": [
            {
              "title": "Was physical and systemic examination performed",
              "description": "Confirmation of physical and systemic examination",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            }
          ]
        },
        {
          "name": "URINE PREGNANCY TEST",
          "route": "baseline-pregnancy-declaration",
          "fields": [
            {
              "title": "Date of Last Menstrual Period (LMP)",
              "description": "Date of last menstrual period",
              "type": "date" as const,
              "required": true
            },
            {
              "title": "Was Urine Pregnancy Test (UPT) done",
              "description": "Whether urine pregnancy test was performed",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            },
            {
              "title": "UPT Result",
              "description": "Result of urine pregnancy test",
              "type": "radio-button" as const,
              "dependencyFieldName": "was_urine_pregnancy_test_(upt)_done",
              "hasDependency": true,
              "values": [
                {
                  "value": "Positive",
                  "label": "Positive"
                },
                {
                  "value": "Negative",
                  "label": "Negative"
                }
              ],
              "required": true
            }
          ]
        },
        {
          "name": "LABORATORY INVESTIGATION",
          "route": "baseline-lab-investigation",
          "fields": [
            {
              "title": "Was Laboratory Investigation performed",
              "description": "Confirmation of laboratory investigation",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            }
          ]
        },
        {
          "name": "ANTHROPOMETRIC DATA",
          "route": "baseline-anthropometric-data",
          "fields": [
            {
              "title": "Weight",
              "description": "Subject's weight in kilograms",
              "type": "number" as const,
              "unit": "kg",
              "required": true
            },
            {
              "title": "Height",
              "description": "Subject's height in meters",
              "type": "number" as const,
              "unit": "m",
              "required": true
            },
            {
              "title": "Waist circumference (part of IDRS Assessment)",
              "description": "Subject's waist circumference in centimeters",
              "type": "number" as const,
              "unit": "cm",
              "required": true
            },
            {
              "title": "Body Mass Index [BMI = Weight (kg)/ Height2 (m)]",
              "description": "Calculated Body Mass Index",
              "type": "calculated" as const,
              "required": true
            }
          ]
        },
        {
          "name": "DIET ASSESSMENT",
          "route": "baseline-diet-assessment",
          "fields": [
            {
              "title": "Was the Diet Assessment done using the 24-Hour Diet Recall Checklist",
              "description": "Confirmation of diet assessment completion",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            }
          ]
        },
        {
          "name": "LIFESTYLE ASSESSMENT",
          "route": "baseline-lifestyle-assessment",
          "fields": [
            {
              "title": "Was the Lifestyle Assessment done using the FANTASTIC Lifestyle Checklist",
              "description": "Confirmation of lifestyle assessment completion",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            }
          ]
        },
        {
          "name": "DETAILS OF INTERVENTION",
          "route": "baseline-intervention-details",
          "fields": [
            {
              "title": "ADT - Ayurveda Diet Therapy: Health drink - asana pānaka",
              "description": "Confirmation of ADT health drink intervention",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            },
            {
              "title": "ADT - Ayurveda Diet Therapy: Triphala tablets",
              "description": "Confirmation of ADT Triphala tablets intervention",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            },
            {
              "title": "ADT - Ayurveda Diet Therapy: Dietary guidelines",
              "description": "Confirmation of ADT dietary guidelines intervention",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            },
            {
              "title": "ABLI - Ayurveda Behavioural Lifestyle Intervention: Udvartana",
              "description": "Confirmation of ABLI Udvartana intervention",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            },
            {
              "title": "ABLI - Ayurveda Behavioural Lifestyle Intervention: Lifestyle guidelines",
              "description": "Confirmation of ABLI lifestyle guidelines intervention",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            }
          ]
        },
        {
          "name": "STUDY PRODUCT/ADT-ABLI KIT DISPENSING RECORD",
          "route": "baseline-product-dispensing",
          "fields": [
            {
              "title": "Were all the Study Products dispensed to the subject as per protocol",
              "description": "Confirmation of study product dispensing",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            },
            {
              "title": "Was the Product Consumption Guide given to the patient",
              "description": "Confirmation of product consumption guide distribution",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            },
            {
              "title": "Was the Daily Product Consumption Log given to the subject",
              "description": "Confirmation of daily product consumption log distribution",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            },
            {
              "title": "Date of dispensing of the Study Products",
              "description": "Date when study products were dispensed",
              "type": "date" as const,
              "required": true
            },
            {
              "title": "i. Asana Churna herbal drink",
              "description": "Asana Churna herbal drink dispensing details",
              "type": "title" as const,
              "required": false
            },
            {
              "title": "Total Number of sachets/boxes dispensed in this visit",
              "description": "Total number of sachets or boxes dispensed for Asana Churna",
              "type": "number" as const,
              "required": true
            },
            {
              "title": "Total Study Product quantity dispensed in this visit",
              "description": "Total quantity of Asana Churna dispensed in grams",
              "type": "number" as const,
              "unit": "g",
              "required": true
            },
            {
              "title": "Batch Number",
              "description": "Batch number of the Asana Churna product",
              "type": "text" as const,
              "required": true
            },
            {
              "title": "Date of Expiry",
              "description": "Expiry date of the Asana Churna product",
              "type": "date" as const,
              "required": true
            },
            {
              "title": "ii. Triphala tablet",
              "description": "Triphala tablet dispensing details",
              "type": "title" as const,
              "required": false
            },
            {
              "title": "Total Number of sachets/boxes dispensed in this visit",
              "description": "Total number of sachets or boxes dispensed for Triphala",
              "type": "number" as const,
              "required": true
            },
            {
              "title": "Total Study Product quantity dispensed in this visit",
              "description": "Total quantity of Triphala dispensed in grams",
              "type": "number" as const,
              "unit": "g",
              "required": true
            },
            {
              "title": "Batch Number",
              "description": "Batch number of the Triphala product",
              "type": "text" as const,
              "required": true
            },
            {
              "title": "Date of Expiry",
              "description": "Expiry date of the Triphala product",
              "type": "date" as const,
              "required": true
            },
            {
              "title": "iii. Yava Churna",
              "description": "Yava Churna dispensing details",
              "type": "title" as const,
              "required": false
            },
            {
              "title": "Total Number of sachets/boxes dispensed in this visit",
              "description": "Total number of sachets or boxes dispensed for Yava Churna",
              "type": "number" as const,
              "required": true
            },
            {
              "title": "Total Study Product quantity dispensed in this visit",
              "description": "Total quantity of Yava Churna dispensed in grams",
              "type": "number" as const,
              "unit": "g",
              "required": true
            },
            {
              "title": "Batch Number",
              "description": "Batch number of the Yava Churna product",
              "type": "text" as const,
              "required": true
            },
            {
              "title": "Date of Expiry",
              "description": "Expiry date of the Yava Churna product",
              "type": "date" as const,
              "required": true
            },
            {
              "title": "Was the dietary consumption guidelines document given to the subject",
              "description": "Confirmation of dietary guidelines document distribution",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            },
            {
              "title": "2. Was the lifestyle guidelines document given to the subject",
              "description": "Confirmation of lifestyle guidelines document distribution",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            }
          ]
        },
        {
          "name": "REVIEW OF ADVERSE EVENTS AND CONCOMITANT MEDICATION",
          "route": "baseline-adverse-events",
          "fields": [
            {
              "title": "1. Did the subject experience any adverse event since last visit",
              "description": "Confirmation of adverse events since last visit",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            },
            {
              "title": "2. Did the subject take any new or change in concomitant medications since last visit",
              "description": "Confirmation of concomitant medication changes",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            }
          ]
        },
        {
          "name": "END OF BASELINE VISIT",
          "route": "baseline-end-visit",
          "fields": [
            {
              "title": "Principal Investigator's Signature",
              "description": "Principal Investigator's signature for baseline visit completion",
              "type": "signature" as const,
              "required": true
            },
            {
              "title": "Date",
              "description": "Date of baseline visit completion",
              "type": "date" as const,
              "required": true
            }
          ]
        }
      ]
    },
    {
      "name": "Medicine Dispensing Visit",
      "icon": "fa fa-medkit",
      "color": "#9C27B0",
      "description": "Study medication dispensing and monitoring",
      "title": "Medicine Dispensing Visit",
      "subTitle": "Medication dispensing and monitoring",
      "forms": [
        {
          "name": "MEDICINE DISPENSING VISIT DATE",
          "route": "medicine-dispensing-visit-date",
          "fields": [
            {
              "title": "Medicine Dispensing Visit No",
              "description": "Medicine dispensing visit number",
              "type": "number" as const,
              "required": true
            },
            {
              "title": "Date of Visit",
              "description": "Date of the medicine dispensing visit",
              "type": "date" as const,
              "required": true
            }
          ]
        },
        {
          "name": "VITAL SIGNS",
          "route": "medicine-vital-signs",
          "fields": [
            {
              "title": "1. Was Vital Signs evaluation performed?",
              "description": "Confirmation of vital signs evaluation",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            }
          ]
        },
        {
          "name": "URINE PREGNANCY TEST",
          "route": "medicine-pregnancy-declaration",
          "fields": [
            {
              "title": "Date of Last Menstrual Period (LMP)",
              "description": "Date of last menstrual period",
              "type": "date" as const,
              "required": true
            },
            {
              "title": "Was Urine Pregnancy Test (UPT) done",
              "description": "Whether urine pregnancy test was performed",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            },
            {
              "title": "UPT Result",
              "description": "Result of urine pregnancy test",
              "type": "radio-button" as const,
              "dependencyFieldName": "was_urine_pregnancy_test_(upt)_done",
              "hasDependency": true,
              "values": [
                {
                  "value": "Positive",
                  "label": "Positive"
                },
                {
                  "value": "Negative",
                  "label": "Negative"
                }
              ],
              "required": true
            }
          ]
        },
        {
          "name": "ANTHROPOMETRIC DATA",
          "route": "medicine-anthropometric-data",
          "fields": [
            {
              "title": "Weight (kg)",
              "description": "Subject's weight in kilograms",
              "type": "text" as const,
              "required": true
            },
            {
              "title": "Height (m)",
              "description": "Subject's height in meters",
              "type": "text" as const,
              "required": true
            },
            {
              "title": "Waist Circumference (cm) (Part of IDRS Assessment)",
              "description": "Subject's waist circumference in centimeters",
              "type": "text" as const,
              "required": true
            },
            {
              "title": "Weight-loss from previous visit (kg)",
              "description": "Weight loss from previous visit in kilograms",
              "type": "number" as const,
              "required": true
            },
            {
              "title": "Weight-loss since baseline visit (kg)",
              "description": "Weight loss since baseline visit in kilograms",
              "type": "number" as const,
              "required": true
            },
            {
              "title": "Difference in Waist circumference from previous visit (cm)",
              "description": "Difference in waist circumference from previous visit",
              "type": "number" as const,
              "required": true
            },
            {
              "title": "Difference in Waist circumference since baseline visit (cm)",
              "description": "Difference in waist circumference since baseline visit",
              "type": "number" as const,
              "required": true
            }
          ]
        },
        {
          "name": "STUDY PRODUCT RETRIEVAL",
          "route": "medicine-product-retrieval",
          "fields": [
            {
              "title": "Was the Study Product retrieved from subject as per protocol?",
              "description": "Confirmation of study product retrieval",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            }
          ]
        },
        {
          "name": "STUDY PRODUCT ACCOUNTABILITY",
          "route": "medicine-product-accountability",
          "fields": [
            {
              "title": "i. Asana Churna herbal drink",
              "description": "Asana Churna herbal drink section",
              "type": "title" as const,
              "required": false
            },
            {
              "title": "Total Number of sachets/boxes dispensed in previous visit",
              "description": "Total number of Asana Churna sachets/boxes dispensed in previous visit",
              "type": "number" as const,
              "required": true
            },
            {
              "title": "Total Study Product quantity dispensed in previous visit (g)",
              "description": "Total Asana Churna quantity dispensed in previous visit in grams",
              "type": "number" as const,
              "required": true
            },
            {
              "title": "Total Number of sachets/boxes retrieved in current visit",
              "description": "Total number of Asana Churna sachets/boxes retrieved in current visit",
              "type": "number" as const,
              "required": true
            },
            {
              "title": "ii. Triphala tablet",
              "description": "Triphala tablet section",
              "type": "title" as const,
              "required": false
            },
            {
              "title": "Total Number of sachets/boxes dispensed in previous visit",
              "description": "Total number of Triphala tablet sachets/boxes dispensed in previous visit",
              "type": "number" as const,
              "required": true
            },
            {
              "title": "Total Study Product quantity dispensed in previous visit (g)",
              "description": "Total Triphala tablet quantity dispensed in previous visit in grams",
              "type": "number" as const,
              "required": true
            },
            {
              "title": "Total Number of sachets/boxes retrieved in current visit",
              "description": "Total number of Triphala tablet sachets/boxes retrieved in current visit",
              "type": "number" as const,
              "required": true
            },
            {
              "title": "iii. Yava Churna",
              "description": "Yava Churna section",
              "type": "title" as const,
              "required": false
            },
            {
              "title": "Total Number of sachets/boxes dispensed in previous visit",
              "description": "Total number of Yava Churna sachets/boxes dispensed in previous visit",
              "type": "number" as const,
              "required": true
            },
            {
              "title": "Total Study Product quantity dispensed in previous visit (g)",
              "description": "Total Yava Churna quantity dispensed in previous visit in grams",
              "type": "number" as const,
              "required": true
            },
            {
              "title": "Total Number of sachets/boxes retrieved in current visit",
              "description": "Total number of Yava Churna sachets/boxes retrieved in current visit",
              "type": "number" as const,
              "required": true
            }
          ]
        },
        {
          "name": "STUDY PRODUCT COMPLIANCE",
          "route": "medicine-product-compliance",
          "fields": [
            {
              "title": "i. Asana Churna herbal drink",
              "description": "Asana Churna herbal drink section",
              "type": "title" as const,
              "required": false
            },
            {
              "title": "Is the subject compliant with the usage of Study Product as per the protocol?",
              "description": "Confirmation of Asana Churna compliance",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            },
            {
              "title": "Date of first Study Product consumed",
              "description": "Date of first Asana Churna consumption",
              "type": "date" as const,
              "required": true
            },
            {
              "title": "Date of last Study Product consumed",
              "description": "Date of last Asana Churna consumption",
              "type": "date" as const,
              "required": true
            },
            {
              "title": "Total quantity of Study Product consumed (g)",
              "description": "Total quantity of Asana Churna consumed in grams",
              "type": "number" as const,
              "required": true
            },
            {
              "title": "Total quantity of Study Product should ideally be consumed by subject (g):",
              "description": "Total ideal quantity of Asana Churna that should be consumed in grams",
              "type": "number" as const,
              "required": true
            },
            {
              "title": "Total quantity missed:",
              "description": "Total quantity of Asana Churna missed",
              "type": "number" as const,
              "required": true
            },
            {
              "title": "Compliance (%):",
              "description": "Asana Churna compliance percentage",
              "type": "text" as const,
              "required": true
            },
            {
              "title": "ii. Triphala tablet",
              "description": "Triphala tablet section",
              "type": "title" as const,
              "required": false
            },
            {
              "title": "Is the subject compliant with the usage of Study Product as per the protocol?",
              "description": "Confirmation of Triphala tablet compliance",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            },
            {
              "title": "Date of first Study Product consumed",
              "description": "Date of first Triphala tablet consumption",
              "type": "date" as const,
              "required": true
            },
            {
              "title": "Date of last Study Product consumed",
              "description": "Date of last Triphala tablet consumption",
              "type": "date" as const,
              "required": true
            },
            {
              "title": "Total quantity of Study Product consumed (g)",
              "description": "Total quantity of Triphala tablet consumed in grams",
              "type": "number" as const,
              "required": true
            },
            {
              "title": "Total quantity of Study Product should ideally be consumed by subject (g):",
              "description": "Total ideal quantity of Triphala tablet that should be consumed in grams",
              "type": "number" as const,
              "required": true
            },
            {
              "title": "Total quantity missed:",
              "description": "Total quantity of Triphala tablet missed",
              "type": "number" as const,
              "required": true
            },
            {
              "title": "Compliance (%):",
              "description": "Triphala tablet compliance percentage",
              "type": "text" as const,
              "required": true
            },
            {
              "title": "iii. Yava Churna",
              "description": "Yava Churna section",
              "type": "title" as const,
              "required": false
            },
            {
              "title": "Is the subject compliant with the usage of Study Product as per the protocol?",
              "description": "Confirmation of Yava Churna compliance",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            },
            {
              "title": "Date of first Study Product consumed",
              "description": "Date of first Yava Churna consumption",
              "type": "date" as const,
              "required": true
            },
            {
              "title": "Date of last Study Product consumed",
              "description": "Date of last Yava Churna consumption",
              "type": "date" as const,
              "required": true
            },
            {
              "title": "Total quantity of Study Product consumed (g)",
              "description": "Total quantity of Yava Churna consumed in grams",
              "type": "number" as const,
              "required": true
            },
            {
              "title": "Total quantity of Study Product should ideally be consumed by subject (g):",
              "description": "Total ideal quantity of Yava Churna that should be consumed in grams",
              "type": "number" as const,
              "required": true
            },
            {
              "title": "Total quantity missed:",
              "description": "Total quantity of Yava Churna missed",
              "type": "number" as const,
              "required": true
            },
            {
              "title": "Compliance (%):",
              "description": "Yava Churna compliance percentage",
              "type": "text" as const,
              "required": true
            }
          ]
        },
        {
          "name": "STUDY PRODUCT DISPENSING RECORD",
          "route": "medicine-product-dispensing-record",
          "fields": [
            {
              "title": "Were all the Study Products dispensed to the subject as per protocol?",
              "description": "Confirmation of study product dispensing as per protocol",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            },
            {
              "title": "Was the Daily Product Consumption Log given to the subject?",
              "description": "Confirmation of daily product consumption log distribution",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            },
            {
              "title": "Date of dispensing of the Study Products:",
              "description": "Date when study products were dispensed",
              "type": "date" as const,
              "required": true
            },
            {
              "title": "i. Asana Churna herbal drink",
              "description": "Asana Churna herbal drink section",
              "type": "title" as const,
              "required": false
            },
            {
              "title": "Total Number of sachets/boxes dispensed in this visit:",
              "description": "Total number of Asana Churna sachets/boxes dispensed in this visit",
              "type": "number" as const,
              "required": true
            },
            {
              "title": "Total Study Product quantity dispensed in this visit (g):",
              "description": "Total Asana Churna quantity dispensed in this visit in grams",
              "type": "number" as const,
              "required": true
            },
            {
              "title": "Batch Number:",
              "description": "Asana Churna batch number",
              "type": "number" as const,
              "required": true
            },
            {
              "title": "Date of Expiry:",
              "description": "Asana Churna expiry date",
              "type": "date" as const,
              "required": true
            },
            {
              "title": "ii. Triphala tablet",
              "description": "Triphala tablet section",
              "type": "title" as const,
              "required": false
            },
            {
              "title": "Total Number of sachets/boxes dispensed in this visit:",
              "description": "Total number of Triphala tablet sachets/boxes dispensed in this visit",
              "type": "number" as const,
              "required": true
            },
            {
              "title": "Total Study Product quantity dispensed in this visit (g):",
              "description": "Total Triphala tablet quantity dispensed in this visit in grams",
              "type": "number" as const,
              "required": true
            },
            {
              "title": "Batch Number:",
              "description": "Triphala tablet batch number",
              "type": "number" as const,
              "required": true
            },
            {
              "title": "Date of Expiry:",
              "description": "Triphala tablet expiry date",
              "type": "date" as const,
              "required": true
            },
            {
              "title": "iii. Yava Churna",
              "description": "Yava Churna section",
              "type": "title" as const,
              "required": false
            },
            {
              "title": "Total Number of sachets/boxes dispensed in this visit:",
              "description": "Total number of Yava Churna sachets/boxes dispensed in this visit",
              "type": "number" as const,
              "required": true
            },
            {
              "title": "Total Study Product quantity dispensed in this visit (g):",
              "description": "Total Yava Churna quantity dispensed in this visit in grams",
              "type": "number" as const,
              "required": true
            },
            {
              "title": "Batch Number:",
              "description": "Yava Churna batch number",
              "type": "number" as const,
              "required": true
            },
            {
              "title": "Date of Expiry:",
              "description": "Yava Churna expiry date",
              "type": "date" as const,
              "required": true
            }
          ]
        },
        {
          "name": "REVIEW OF ADVERSE EVENTS AND CONCOMITANT MEDICATION",
          "route": "medicine-adverse-events",
          "fields": [
            {
              "title": "Did the subject experience any adverse event since last visit?",
              "description": "Confirmation of adverse events since last visit",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            },
            {
              "title": "Did the subject take any new or change in concomitant medications since last visit?",
              "description": "Confirmation of concomitant medication changes",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            }
          ]
        },
        {
          "name": "END OF THE VISIT",
          "route": "medicine-end-visit",
          "fields": [
            {
              "title": "Principal Investigator's Signature",
              "description": "Principal Investigator's signature for medicine dispensing visit completion",
              "type": "text" as const,
              "required": true
            },
            {
              "title": "Date",
              "description": "Date of medicine dispensing visit completion",
              "type": "date" as const,
              "required": true
            }
          ]
        }
      ]
    },
    {
      "name": "Follow-up Visit",
      "icon": "fa fa-calendar-check-o",
      "color": "#607D8B",
      "description": "Regular follow-up visits and monitoring",
      "title": "Follow-up Visit",
      "subTitle": "Regular monitoring and follow-up",
      "forms": [
        {
          "name": "FOLLOW-UP-VISIT (Every 4th Month)",
          "route": "follow-up-visit",
          "fields": [
            {
              "title": "1. Follow Up Visit No:",
              "description": "Follow-up visit number",
              "type": "number" as const,
              "required": true
            },
            {
              "title": "Date of Visit",
              "description": "Date of the follow-up visit",
              "type": "date" as const,
              "required": true
            }
          ]
        },
        {
          "name": "VITAL SIGNS",
          "route": "follow-up-vital-signs",
          "fields": [
            {
              "title": "1. Was Vital Signs evaluation performed",
              "description": "Confirmation of vital signs evaluation",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            }
          ]
        },
        {
          "name": "PHYSICAL AND SYSTEMIC EXAMINATION",
          "route": "follow-up-physical",
          "fields": [
            {
              "title": "1. Was physical and systematic examination performed",
              "description": "Confirmation of physical and systematic examination",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            }
          ]
        },
        {
          "name": "LABORATORY INVESTIGATION",
          "route": "follow-up-laboratory-investigation",
          "fields": [
            {
              "title": "1. Was Laboratory Investigation performed",
              "description": "Confirmation of laboratory investigation",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            },
            {
              "title": "2. Date of Sample Collection:",
              "description": "Date of sample collection for laboratory investigation",
              "type": "date" as const,
              "required": false
            }
          ]
        },
        {
          "name": "URINE PREGNANCY TEST",
          "route": "follow-up-pregnancy-declaration",
          "fields": [
            {
              "title": "Date of Last Menstrual Period (LMP)",
              "description": "Date of last menstrual period",
              "type": "date" as const,
              "required": true
            },
            {
              "title": "Was Urine Pregnancy Test (UPT) done",
              "description": "Whether urine pregnancy test was performed",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "yes",
                  "label": "Yes"
                },
                {
                  "value": "no",
                  "label": "No"
                }
              ],
              "required": true
            },
            {
              "title": "UPT Result",
              "description": "Result of urine pregnancy test",
              "type": "radio-button" as const,
              "dependencyFieldName": "was_urine_pregnancy_test_(upt)_done",
              "hasDependency": true,
              "values": [
                {
                  "value": "Positive",
                  "label": "Positive"
                },
                {
                  "value": "Negative",
                  "label": "Negative"
                }
              ],
              "required": true
            }
          ]
        },
        {
          "name": "ANTHROPOMETRIC DATA",
          "route": "follow-up-anthropometric-data",
          "fields": [
            {
              "title": "Weight (kg)",
              "description": "Subject's weight in kilograms",
              "type": "number" as const,
              "unit": "kg",
              "required": true
            },
            {
              "title": "Height (m)",
              "description": "Subject's height in meters",
              "type": "number" as const,
              "unit": "m",
              "required": true
            },
            {
              "title": "3. Waist Circumference (cm):",
              "description": "Subject's waist circumference in centimeters",
              "type": "number" as const,
              "unit": "cm",
              "required": true
            },
            {
              "title": "Weight-loss from previous visit (kg)",
              "description": "Weight loss from previous visit in kilograms",
              "type": "number" as const,
              "unit": "kg",
              "required": true
            },
            {
              "title": "Weight-loss since baseline visit (kg)",
              "description": "Weight loss since baseline visit in kilograms",
              "type": "number" as const,
              "unit": "kg",
              "required": true
            },
            {
              "title": "Difference in Waist circumference from previous visit (cm)",
              "description": "Difference in waist circumference from previous visit",
              "type": "number" as const,
              "unit": "cm",
              "required": true
            },
            {
              "title": "Difference in Waist circumference since baseline visit (cm)",
              "description": "Difference in waist circumference since baseline visit",
              "type": "number" as const,
              "unit": "cm",
              "required": true
            },
            {
              "title": "8. Body Mass Index (BMI):",
              "description": "Calculated Body Mass Index",
              "type": "calculated" as const,
              "required": true
            }
          ]
        },
        {
          "name": "STUDY PRODUCT RETRIEVAL",
          "route": "follow-up-product-retrieval",
          "fields": [
            {
              "title": "Study Product Retrieval Table",
              "description": "Table for study product retrieval with columns: Product Name, Quantity Dispensed, Quantity Retrieved, Quantity Consumed, Compliance %",
              "type": "table" as const,
              "columns": [
                {
                  "title": "Product Name",
                  "type": "text" as const,
                  "required": true
                },
                {
                  "title": "Quantity Dispensed",
                  "type": "number" as const,
                  "required": true
                },
                {
                  "title": "Quantity Retrieved",
                  "type": "number" as const,
                  "required": true
                },
                {
                  "title": "Quantity Consumed",
                  "type": "number" as const,
                  "required": true
                },
                {
                  "title": "Compliance %",
                  "type": "number" as const,
                  "required": true
                }
              ],
              "required": true
            }
          ]
        },
        {
          "name": "STUDY PRODUCT ACCOUNTABILITY",
          "route": "follow-up-product-accountability",
          "fields": [
            {
              "title": "Study Product Accountability Table",
              "description": "Table for study product accountability with columns: Product Name, Batch Number, Expiry Date, Quantity Dispensed, Quantity Returned, Discrepancy",
              "type": "table" as const,
              "columns": [
                {
                  "title": "Product Name",
                  "type": "text" as const,
                  "required": true
                },
                {
                  "title": "Batch Number",
                  "type": "text" as const,
                  "required": true
                },
                {
                  "title": "Expiry Date",
                  "type": "date" as const,
                  "required": true
                },
                {
                  "title": "Quantity Dispensed",
                  "type": "number" as const,
                  "required": true
                },
                {
                  "title": "Quantity Returned",
                  "type": "number" as const,
                  "required": true
                },
                {
                  "title": "Discrepancy",
                  "type": "number" as const,
                  "required": false
                }
              ],
              "required": true
            }
          ]
        },
        {
          "name": "STUDY PRODUCT COMPLIANCE",
          "route": "follow-up-product-compliance",
          "fields": [
            {
              "title": "Study Product Compliance Table",
              "description": "Table for study product compliance with columns: Product Name, Expected Consumption, Actual Consumption, Compliance %, Comments",
              "type": "table" as const,
              "columns": [
                {
                  "title": "Product Name",
                  "type": "text" as const,
                  "required": true
                },
                {
                  "title": "Expected Consumption",
                  "type": "number" as const,
                  "required": true
                },
                {
                  "title": "Actual Consumption",
                  "type": "number" as const,
                  "required": true
                },
                {
                  "title": "Compliance %",
                  "type": "number" as const,
                  "required": true
                },
                {
                  "title": "Comments",
                  "type": "textarea" as const,
                  "required": false
                }
              ],
              "required": true
            }
          ]
        },
        {
          "name": "STUDY PRODUCT DISPENSING RECORD",
          "route": "follow-up-product-dispensing-record",
          "fields": [
            {
              "title": "Study Product Dispensing Table",
              "description": "Table for study product dispensing with columns: Product Name, Quantity, Batch Number, Expiry Date, Dispensing Date",
              "type": "table" as const,
              "columns": [
                {
                  "title": "Product Name",
                  "type": "text" as const,
                  "required": true
                },
                {
                  "title": "Quantity",
                  "type": "number" as const,
                  "required": true
                },
                {
                  "title": "Batch Number",
                  "type": "text" as const,
                  "required": true
                },
                {
                  "title": "Expiry Date",
                  "type": "date" as const,
                  "required": true
                },
                {
                  "title": "Dispensing Date",
                  "type": "date" as const,
                  "required": true
                }
              ],
              "required": true
            }
          ]
        },
        {
          "name": "REVIEW OF ADVERSE EVENTS AND CONCOMITANT MEDICATION",
          "route": "follow-up-adverse-events",
          "fields": [
            {
              "title": "Adverse Events Review Table",
              "description": "Table for adverse events review with columns: Event Term, Onset Date, End Date, Severity, Action Taken, Outcome",
              "type": "table" as const,
              "columns": [
                {
                  "title": "Event Term",
                  "type": "text" as const,
                  "required": true
                },
                {
                  "title": "Onset Date",
                  "type": "date" as const,
                  "required": true
                },
                {
                  "title": "End Date",
                  "type": "date" as const,
                  "required": false
                },
                {
                  "title": "Severity",
                  "type": "dropdown" as const,
                  "values": [
                    {
                      "value": "1",
                      "label": "Mild"
                    },
                    {
                      "value": "2",
                      "label": "Moderate"
                    },
                    {
                      "value": "3",
                      "label": "Severe"
                    }
                  ],
                  "required": true
                },
                {
                  "title": "Action Taken",
                  "type": "textarea" as const,
                  "required": false
                },
                {
                  "title": "Outcome",
                  "type": "dropdown" as const,
                  "values": [
                    {
                      "value": "1",
                      "label": "Resolved"
                    },
                    {
                      "value": "2",
                      "label": "Resolving"
                    },
                    {
                      "value": "3",
                      "label": "Not Resolved"
                    },
                    {
                      "value": "4",
                      "label": "Resolved with sequelae"
                    }
                  ],
                  "required": true
                }
              ],
              "required": true
            }
          ]
        }
      ]
    },
    {
      "name": "Study Completion / Termination Record",
      "icon": "fa fa-flag-checkered",
      "color": "#F44336",
      "description": "Study completion or early termination documentation",
      "title": "Study Completion / Termination Record",
      "subTitle": "Study completion or termination",
      "forms": [
        {
          "name": "STUDY COMPLETION / TERMINATION RECORD",
          "route": "study-completion-termination-record",
          "fields": [
            {
              "title": "Did the Subject complete the study?",
              "description": "Mark Yes if the subject completed the study, No otherwise",
              "type": "radio-button" as const,
              "values": [
                {
                  "value": "Yes",
                  "label": "Yes"
                },
                {
                  "value": "No",
                  "label": "No"
                }
              ],
              "required": true
            },
            {
              "title": "Provide comments",
              "description": "Comments if subject did not complete the study",
              "type": "textarea" as const,
              "required": false
            },
            {
              "title": "Date of Study Completion",
              "description": "The date when the subject completed the study",
              "type": "date" as const,
              "required": true
            },
            {
              "title": "The Date of Study Discontinuation or Screen Failure:",
              "description": "The date when the subject discontinued the study",
              "type": "date" as const,
              "required": true
            },
            {
              "title": "Study Completion Certificate",
              "description": "Upload the study completion certificate",
              "type": "text" as const,
              "required": true
            }
          ]
        },
        {
          "name": "REASON FOR DISCONTINUATION",
          "route": "reason-for-discontinuation",
          "fields": [
            {
              "title": "Reason for Discontinuation",
              "description": "Reason for discontinuing the study",
              "type": "dropdown" as const,
              "values": [
                {
                  "value": "Adverse Event",
                  "label": "Adverse Event"
                },
                {
                  "value": "Protocol Deviation",
                  "label": "Protocol Deviation"
                },
                {
                  "value": "Withdrawal of Consent",
                  "label": "Withdrawal of Consent"
                },
                {
                  "value": "Lost to Follow-up",
                  "label": "Lost to Follow-up"
                },
                {
                  "value": "Death",
                  "label": "Death"
                }
              ],
              "required": true
            },
            {
              "title": "Comments / Cause of Death",
              "description": "Comments or cause of death",
              "type": "textarea" as const,
              "required": true
            },
            {
              "title": "Date of Death",
              "description": "Date of death if applicable",
              "type": "date" as const,
              "required": true
            },
            {
              "title": "Supporting Documentation",
              "description": "Upload any supporting documentation for discontinuation",
              "type": "textarea" as const,
              "required": false
            }
          ]
        }
      ]
    },
    {
      "name": "Adverse Events Records Log",
      "icon": "fa fa-exclamation-triangle",
      "color": "#FF5722",
      "description": "Documentation of adverse events during the study",
      "title": "Adverse Events Records Log",
      "subTitle": "Adverse events documentation",
      "forms": [
        {
          "name": "ADVERSE EVENT REPORT",
          "route": "adverse-event-report",
          "fields": [
            {
              "title": "Adverse Events Table",
              "description": "Table for adverse events with columns: Log Line, Adverse Event Term, Onset Date, End Date, Medication Taken (Y/N), Ongoing, Severity, Relationship to Study Drug, Action, Outcome, Dropped due to this AE?, Reported as SAE?",
              "type": "table" as const,
              "columns": [
                {
                  "title": "Log Line",
                  "type": "text" as const,
                  "required": true
                },
                {
                  "title": "Adverse Event Term",
                  "type": "text" as const,
                  "required": true
                },
                {
                  "title": "Onset Date",
                  "type": "date" as const,
                  "required": true
                },
                {
                  "title": "End Date",
                  "type": "date" as const,
                  "required": false
                },
                {
                  "title": "Medication Taken (Y/N)",
                  "type": "dropdown" as const,
                  "values": [
                    {
                      "value": "Yes",
                      "label": "Yes"
                    },
                    {
                      "value": "No",
                      "label": "No"
                    }
                  ],
                  "required": true
                },
                {
                  "title": "Ongoing",
                  "type": "dropdown" as const,
                  "values": [
                    {
                      "value": "Yes",
                      "label": "Yes"
                    },
                    {
                      "value": "No",
                      "label": "No"
                    }
                  ],
                  "required": true
                },
                {
                  "title": "Severity",
                  "type": "dropdown" as const,
                  "values": [
                    {
                      "value": "1",
                      "label": "Mild"
                    },
                    {
                      "value": "2",
                      "label": "Moderate"
                    },
                    {
                      "value": "3",
                      "label": "Severe"
                    }
                  ],
                  "required": true
                },
                {
                  "title": "Relationship to Study Drug",
                  "type": "dropdown" as const,
                  "values": [
                    {
                      "value": "1",
                      "label": "Unrelated"
                    },
                    {
                      "value": "2",
                      "label": "Unlikely"
                    },
                    {
                      "value": "3",
                      "label": "Possible"
                    },
                    {
                      "value": "4",
                      "label": "Probable"
                    },
                    {
                      "value": "5",
                      "label": "Definite"
                    }
                  ],
                  "required": true
                },
                {
                  "title": "Action",
                  "type": "dropdown" as const,
                  "values": [
                    {
                      "value": "1",
                      "label": "Study drug Withdrawn"
                    },
                    {
                      "value": "2",
                      "label": "Study drug Dose Reduced"
                    },
                    {
                      "value": "3",
                      "label": "Study drug Dose Increased"
                    },
                    {
                      "value": "4",
                      "label": "Study drug Dose Not Changed"
                    },
                    {
                      "value": "5",
                      "label": "Unknown"
                    },
                    {
                      "value": "6",
                      "label": "Not Applicable"
                    }
                  ],
                  "required": true
                },
                {
                  "title": "Outcome",
                  "type": "dropdown" as const,
                  "values": [
                    {
                      "value": "1",
                      "label": "Resolved"
                    },
                    {
                      "value": "2",
                      "label": "Resolving"
                    },
                    {
                      "value": "3",
                      "label": "Not Resolved"
                    },
                    {
                      "value": "4",
                      "label": "Resolved with sequelae"
                    },
                    {
                      "value": "5",
                      "label": "Fatal"
                    },
                    {
                      "value": "6",
                      "label": "Unknown"
                    }
                  ],
                  "required": true
                },
                {
                  "title": "Dropped due to this AE?",
                  "type": "dropdown" as const,
                  "values": [
                    {
                      "value": "Yes",
                      "label": "Yes"
                    },
                    {
                      "value": "No",
                      "label": "No"
                    }
                  ],
                  "required": true
                },
                {
                  "title": "Reported as SAE?",
                  "type": "dropdown" as const,
                  "values": [
                    {
                      "value": "Yes",
                      "label": "Yes"
                    },
                    {
                      "value": "No",
                      "label": "No"
                    }
                  ],
                  "required": true
                }
              ],
              "required": true
            }
          ]
        }
      ]
    },
    {
      "name": "Principal Investigator's Signature",
      "icon": "fa fa-signature",
      "color": "#795548",
      "description": "Principal investigator signature and authorization",
      "title": "Principal Investigator's Signature",
      "subTitle": "PI signature and authorization",
      "forms": [
        {
          "name": "PRINCIPAL INVESTIGATOR'S SIGNATURE",
          "route": "principal-investigator-signature",
          "fields": [
            {
              "title": "Principal Investigator's Signature",
              "description": "By signing below, I certify that I have reviewed all the data contained within this case report form and that it accurately reflects the course of this Subject on this study. I understand that changes may be made to this data as a result of the data review process. I will keep the sponsor informed about the changes if any.",
              "type": "text" as const,
              "required": true
            },
            {
              "title": "2. Date of PI Signature:",
              "description": "Date of Principal Investigator's signature",
              "type": "date" as const,
              "required": true
            }
          ]
        }
      ]
    },
    {
      "name": "Protocol Deviation / Violation / Waiver Log",
      "icon": "fa fa-exclamation-circle",
      "color": "#E91E63",
      "description": "Documentation of protocol deviations and violations",
      "title": "Protocol Deviation / Violation / Waiver Log",
      "subTitle": "Protocol deviations and violations",
      "forms": [
        {
          "name": "PROTOCOL DEVIATION/ VIOLATION/ WAIVER LOG",
          "route": "protocol-deviation-log",
          "fields": [
            {
              "title": "Protocol Deviation Table",
              "description": "Table for protocol deviations with columns: Log Line, Visit Number, Date of Deviation/Violation/Waiver, Category, Sub Category, Specify If Other, Description, Impact",
              "type": "table" as const,
              "columns": [
                {
                  "title": "Log Line",
                  "type": "text" as const,
                  "required": true
                },
                {
                  "title": "Visit Number",
                  "type": "dropdown" as const,
                  "values": [
                    {
                      "value": "Screening",
                      "label": "Screening"
                    },
                    {
                      "value": "Baseline",
                      "label": "Baseline"
                    },
                    {
                      "value": "Follow-up 1",
                      "label": "Follow-up 1"
                    },
                    {
                      "value": "Follow-up 2",
                      "label": "Follow-up 2"
                    }
                  ],
                  "required": true
                },
                {
                  "title": "Date of Deviation/Violation/Waiver",
                  "type": "date" as const,
                  "required": true
                },
                {
                  "title": "Category",
                  "type": "dropdown" as const,
                  "values": [
                    {
                      "value": "1",
                      "label": "Protocol Deviation"
                    },
                    {
                      "value": "2",
                      "label": "Protocol Violation"
                    },
                    {
                      "value": "3",
                      "label": "Protocol Waiver"
                    }
                  ],
                  "required": true
                },
                {
                  "title": "Sub Category",
                  "type": "dropdown" as const,
                  "values": [
                    {
                      "value": "1",
                      "label": "Informed Consent"
                    },
                    {
                      "value": "2",
                      "label": "Inclusion/Exclusion Criteria"
                    },
                    {
                      "value": "3",
                      "label": "Randomisation"
                    },
                    {
                      "value": "4",
                      "label": "Assessment Deviations"
                    },
                    {
                      "value": "5",
                      "label": "Lab Deviations"
                    },
                    {
                      "value": "6",
                      "label": "Visit Schedule Deviations"
                    },
                    {
                      "value": "7",
                      "label": "Study Product Handling"
                    },
                    {
                      "value": "8",
                      "label": "Treatment Compliance"
                    },
                    {
                      "value": "9",
                      "label": "Other - Specify"
                    }
                  ],
                  "required": true
                },
                {
                  "title": "Specify, If Other",
                  "type": "text" as const,
                  "required": false
                },
                {
                  "title": "Description",
                  "type": "text" as const,
                  "required": true
                },
                {
                  "title": "Impact",
                  "type": "dropdown" as const,
                  "values": [
                    {
                      "value": "1",
                      "label": "Minor"
                    },
                    {
                      "value": "2",
                      "label": "Major"
                    },
                    {
                      "value": "3",
                      "label": "Critical"
                    }
                  ],
                  "required": true
                }
              ],
              "required": true
            }
          ]
        }
      ]
    },
    {
      "name": "Concomitant Medication Log",
      "icon": "fa fa-pills",
      "color": "#00BCD4",
      "description": "Documentation of concomitant medications",
      "title": "Concomitant Medication Log",
      "subTitle": "Concomitant medication tracking",
      "forms": [
        {
          "name": "CONCOMITANT MEDICATION LOG",
          "route": "concomitant-medication-log",
          "fields": [
            {
              "title": "Concomitant Medication Table",
              "description": "Table for concomitant medications with columns: Log Line, Name of the Drug, Indication, Start Date, End Date, Route of Administration, Dosage, Frequency, Ongoing",
              "type": "table" as const,
              "columns": [
                {
                  "title": "Log Line",
                  "type": "text" as const,
                  "required": true
                },
                {
                  "title": "Name of the Drug",
                  "type": "text" as const,
                  "required": true
                },
                {
                  "title": "Indication",
                  "type": "text" as const,
                  "required": true
                },
                {
                  "title": "Start Date",
                  "type": "date" as const,
                  "required": true
                },
                {
                  "title": "End Date",
                  "type": "date" as const,
                  "required": false
                },
                {
                  "title": "Route of Administration",
                  "type": "dropdown" as const,
                  "values": [
                    {
                      "value": "ORL",
                      "label": "Oral"
                    },
                    {
                      "value": "IV",
                      "label": "Intravenous"
                    },
                    {
                      "value": "IM",
                      "label": "Intramuscular"
                    },
                    {
                      "value": "NEB",
                      "label": "Nebulizer"
                    },
                    {
                      "value": "INH",
                      "label": "Inhaler"
                    },
                    {
                      "value": "SC",
                      "label": "Subcutaneous"
                    }
                  ],
                  "required": true
                },
                {
                  "title": "Dosage",
                  "type": "text" as const,
                  "required": true
                },
                {
                  "title": "Frequency",
                  "type": "dropdown" as const,
                  "values": [
                    {
                      "value": "OD",
                      "label": "Once a day"
                    },
                    {
                      "value": "BD",
                      "label": "Twice a day"
                    },
                    {
                      "value": "TID",
                      "label": "Thrice a day"
                    },
                    {
                      "value": "QID",
                      "label": "Four times a day"
                    },
                    {
                      "value": "SOS",
                      "label": "If needed"
                    }
                  ],
                  "required": true
                },
                {
                  "title": "Ongoing",
                  "type": "dropdown" as const,
                  "values": [
                    {
                      "value": "Yes",
                      "label": "Yes"
                    },
                    {
                      "value": "No",
                      "label": "No"
                    }
                  ],
                  "required": true
                }
              ],
              "required": true
            }
          ]
        }
      ]
    },
    {
      "name": "Rescue Medication Log",
      "icon": "fa fa-first-aid",
      "color": "#8BC34A",
      "description": "Documentation of rescue medications",
      "title": "Rescue Medication Log",
      "subTitle": "Rescue medication tracking",
      "forms": [
        {
          "name": "RESCUE MEDICATION LOG",
          "route": "rescue-medication-log",
          "fields": [
            {
              "title": "Rescue Medication Table",
              "description": "Table for rescue medications with columns: Log Line, Name of the Drug, Indication, Start Date, End Date, Route of Administration, Dosage, Frequency, Ongoing",
              "type": "table" as const,
              "columns": [
                {
                  "title": "Log Line",
                  "type": "text" as const,
                  "required": true
                },
                {
                  "title": "Name of the Drug",
                  "type": "text" as const,
                  "required": true
                },
                {
                  "title": "Indication",
                  "type": "text" as const,
                  "required": true
                },
                {
                  "title": "Start Date",
                  "type": "date" as const,
                  "required": true
                },
                {
                  "title": "End Date",
                  "type": "date" as const,
                  "required": false
                },
                {
                  "title": "Route of Administration",
                  "type": "dropdown" as const,
                  "values": [
                    {
                      "value": "ORL",
                      "label": "Oral"
                    },
                    {
                      "value": "IV",
                      "label": "Intravenous"
                    },
                    {
                      "value": "IM",
                      "label": "Intramuscular"
                    },
                    {
                      "value": "NEB",
                      "label": "Nebulizer"
                    },
                    {
                      "value": "INH",
                      "label": "Inhaler"
                    },
                    {
                      "value": "SC",
                      "label": "Subcutaneous"
                    }
                  ],
                  "required": true
                },
                {
                  "title": "Dosage",
                  "type": "text" as const,
                  "required": true
                },
                {
                  "title": "Frequency",
                  "type": "dropdown" as const,
                  "values": [
                    {
                      "value": "OD",
                      "label": "Once a day"
                    },
                    {
                      "value": "BD",
                      "label": "Twice a day"
                    },
                    {
                      "value": "TID",
                      "label": "Thrice a day"
                    },
                    {
                      "value": "QID",
                      "label": "Four times a day"
                    },
                    {
                      "value": "SOS",
                      "label": "If needed"
                    }
                  ],
                  "required": true
                },
                {
                  "title": "Ongoing",
                  "type": "dropdown" as const,
                  "values": [
                    {
                      "value": "Yes",
                      "label": "Yes"
                    },
                    {
                      "value": "No",
                      "label": "No"
                    }
                  ],
                  "required": true
                }
              ],
              "required": true
            }
          ]
        }
      ]
    },
    {
      "name": "Prior Medication Log",
      "icon": "fa fa-history",
      "color": "#FFC107",
      "description": "Documentation of prior medications",
      "title": "Prior Medication Log",
      "subTitle": "Prior medication tracking",
      "forms": [
        {
          "name": "PRIOR MEDICATION LOG",
          "route": "prior-medication-log",
          "fields": [
            {
              "title": "Prior Medication Table",
              "description": "Table for prior medications with columns: Log Line, Name of the Drug, Indication, Start Date, End Date, Route of Administration, Dosage, Frequency, Ongoing",
              "type": "table" as const,
              "columns": [
                {
                  "title": "Log Line",
                  "type": "text" as const,
                  "required": true
                },
                {
                  "title": "Name of the Drug",
                  "type": "text" as const,
                  "required": true
                },
                {
                  "title": "Indication",
                  "type": "text" as const,
                  "required": true
                },
                {
                  "title": "Start Date",
                  "type": "date" as const,
                  "required": true
                },
                {
                  "title": "End Date",
                  "type": "date" as const,
                  "required": false
                },
                {
                  "title": "Route of Administration",
                  "type": "dropdown" as const,
                  "values": [
                    {
                      "value": "ORL",
                      "label": "Oral"
                    },
                    {
                      "value": "IV",
                      "label": "Intravenous"
                    },
                    {
                      "value": "IM",
                      "label": "Intramuscular"
                    },
                    {
                      "value": "NEB",
                      "label": "Nebulizer"
                    },
                    {
                      "value": "INH",
                      "label": "Inhaler"
                    },
                    {
                      "value": "SC",
                      "label": "Subcutaneous"
                    }
                  ],
                  "required": true
                },
                {
                  "title": "Dosage",
                  "type": "text" as const,
                  "required": true
                },
                {
                  "title": "Frequency",
                  "type": "dropdown" as const,
                  "values": [
                    {
                      "value": "OD",
                      "label": "Once a day"
                    },
                    {
                      "value": "BD",
                      "label": "Twice a day"
                    },
                    {
                      "value": "TID",
                      "label": "Thrice a day"
                    },
                    {
                      "value": "QID",
                      "label": "Four times a day"
                    },
                    {
                      "value": "SOS",
                      "label": "If needed"
                    }
                  ],
                  "required": true
                },
                {
                  "title": "Ongoing",
                  "type": "dropdown" as const,
                  "values": [
                    {
                      "value": "Yes",
                      "label": "Yes"
                    },
                    {
                      "value": "No",
                      "label": "No"
                    }
                  ],
                  "required": true
                }
              ],
              "required": true
            }
          ]
        }
      ]
    },
    {
      "name": "Medical History",
      "icon": "fa fa-stethoscope",
      "color": "#3F51B5",
      "description": "Comprehensive medical history documentation",
      "title": "Medical History",
      "subTitle": "Medical history documentation",
      "forms": [
        {
          "name": "MEDICAL HISTORY",
          "route": "medical-history",
          "fields": [
            {
              "title": "Medical History Table",
              "description": "Table for medical history entries with columns: Log Line, Medical Condition, Start Date, End Date, Ongoing, Severity, Medication Taken",
              "type": "table" as const,
              "columns": [
                {
                  "title": "Log Line",
                  "type": "text" as const,
                  "required": true
                },
                {
                  "title": "Medical Condition",
                  "type": "text" as const,
                  "required": true
                },
                {
                  "title": "Start Date",
                  "type": "date" as const,
                  "required": true
                },
                {
                  "title": "End Date",
                  "type": "date" as const,
                  "required": false
                },
                {
                  "title": "Ongoing",
                  "type": "dropdown" as const,
                  "values": [
                    {
                      "value": "Yes",
                      "label": "Yes"
                    },
                    {
                      "value": "No",
                      "label": "No"
                    }
                  ],
                  "required": true
                },
                {
                  "title": "Severity",
                  "type": "dropdown" as const,
                  "values": [
                    {
                      "value": "1",
                      "label": "Mild"
                    },
                    {
                      "value": "2",
                      "label": "Moderate"
                    },
                    {
                      "value": "3",
                      "label": "Severe"
                    },
                    {
                      "value": "4",
                      "label": "Not Applicable"
                    }
                  ],
                  "required": true
                },
                {
                  "title": "Medication Taken",
                  "type": "dropdown" as const,
                  "values": [
                    {
                      "value": "Yes",
                      "label": "Yes"
                    },
                    {
                      "value": "No",
                      "label": "No"
                    }
                  ],
                  "required": true
                }
              ],
              "required": true
            }
          ]
        }
      ]
    },
    {
      "name": "Vital Signs",
      "icon": "fa fa-heartbeat",
      "color": "#FF5722",
      "description": "Vital signs measurements and monitoring",
      "title": "Vital Signs",
      "subTitle": "Vital signs monitoring",
      "forms": [
        {
          "name": "VITAL SIGNS",
          "route": "vital-signs",
          "fields": [
            {
              "title": "Vital Signs Table",
              "description": "Table for vital signs with columns: Parameter, Value, Unit, Normal Range, Status",
              "type": "table" as const,
              "columns": [
                {
                  "title": "Parameter",
                  "type": "text" as const,
                  "required": true
                },
                {
                  "title": "Value",
                  "type": "number" as const,
                  "required": true
                },
                {
                  "title": "Unit",
                  "type": "text" as const,
                  "required": true
                },
                {
                  "title": "Normal Range",
                  "type": "text" as const,
                  "required": false
                },
                {
                  "title": "Status",
                  "type": "dropdown" as const,
                  "values": [
                    {
                      "value": "normal",
                      "label": "Normal"
                    },
                    {
                      "value": "abnormal",
                      "label": "Abnormal"
                    },
                    {
                      "value": "critical",
                      "label": "Critical"
                    }
                  ],
                  "required": true
                }
              ],
              "required": true
            }
          ]
        }
      ]
    },
    {
      "name": "Physical and Systemic Examination",
      "icon": "fa fa-user-md",
      "color": "#009688",
      "description": "Physical and systemic examination results",
      "title": "Physical and Systemic Examination",
      "subTitle": "Physical examination documentation",
      "forms": [
        {
          "name": "PHYSICAL AND SYSTEMIC EXAMINATION",
          "route": "physical-systemic",
          "fields": [
            {
              "title": "Physical Examination Table",
              "description": "Table for physical examination with columns: System, Finding, Normal/Abnormal, Comments",
              "type": "table" as const,
              "columns": [
                {
                  "title": "System",
                  "type": "dropdown" as const,
                  "values": [
                    {
                      "value": "cardiovascular",
                      "label": "Cardiovascular"
                    },
                    {
                      "value": "respiratory",
                      "label": "Respiratory"
                    },
                    {
                      "value": "gastrointestinal",
                      "label": "Gastrointestinal"
                    },
                    {
                      "value": "neurological",
                      "label": "Neurological"
                    },
                    {
                      "value": "musculoskeletal",
                      "label": "Musculoskeletal"
                    },
                    {
                      "value": "integumentary",
                      "label": "Integumentary"
                    }
                  ],
                  "required": true
                },
                {
                  "title": "Finding",
                  "type": "textarea" as const,
                  "required": true
                },
                {
                  "title": "Normal/Abnormal",
                  "type": "dropdown" as const,
                  "values": [
                    {
                      "value": "normal",
                      "label": "Normal"
                    },
                    {
                      "value": "abnormal",
                      "label": "Abnormal"
                    }
                  ],
                  "required": true
                },
                {
                  "title": "Comments",
                  "type": "textarea" as const,
                  "required": false
                }
              ],
              "required": true
            }
          ]
        }
      ]
    },
    {
      "name": "Laboratory Investigation",
      "icon": "fa fa-flask",
      "color": "#673AB7",
      "description": "Laboratory investigation results and reports",
      "title": "Laboratory Investigation",
      "subTitle": "Laboratory results",
      "forms": [
        {
          "name": "LABORATORY INVESTIGATION",
          "route": "laboratory-investigation-log",
          "fields": [
            {
              "title": "Laboratory Investigation Table",
              "description": "Table for laboratory investigation with columns: Test Name, Result, Unit, Reference Range, Status, Comments",
              "type": "table" as const,
              "columns": [
                {
                  "title": "Test Name",
                  "type": "text" as const,
                  "required": true
                },
                {
                  "title": "Result",
                  "type": "text" as const,
                  "required": true
                },
                {
                  "title": "Unit",
                  "type": "text" as const,
                  "required": false
                },
                {
                  "title": "Reference Range",
                  "type": "text" as const,
                  "required": false
                },
                {
                  "title": "Status",
                  "type": "dropdown" as const,
                  "values": [
                    {
                      "value": "normal",
                      "label": "Normal"
                    },
                    {
                      "value": "high",
                      "label": "High"
                    },
                    {
                      "value": "low",
                      "label": "Low"
                    },
                    {
                      "value": "critical",
                      "label": "Critical"
                    }
                  ],
                  "required": true
                },
                {
                  "title": "Comments",
                  "type": "textarea" as const,
                  "required": false
                }
              ],
              "required": true
            }
          ]
        }
      ]
    },
    {
      "name": "IDRS",
      "icon": "fa fa-calculator",
      "color": "#795548",
      "description": "Indian Diabetes Risk Score assessment",
      "title": "IDRS",
      "subTitle": "Diabetes risk assessment",
      "forms": [
        {
          "name": "IDRS Form",
          "route": "idrs-form-log",
          "fields": [
            {
              "title": "IDRS Table",
              "description": "Table for IDRS assessment with columns: Logline No., Assessment Type, Criteria, Criteria Valid, Score to be Given, Score Given",
              "type": "table" as const,
              "columns": [
                {
                  "title": "Logline No.",
                  "type": "text" as const,
                  "required": true
                },
                {
                  "title": "Assessment Type",
                  "type": "text" as const,
                  "required": true
                },
                {
                  "title": "Criteria",
                  "type": "dropdown" as const,
                  "values": [
                    {
                      "value": "Age < 35 years",
                      "label": "Age < 35 years"
                    },
                    {
                      "value": "Age 35-49 years",
                      "label": "Age 35-49 years"
                    },
                    {
                      "value": "Age ≥ 50 years",
                      "label": "Age ≥ 50 years"
                    },
                    {
                      "value": "Abdominal obesity (waist circumference)",
                      "label": "Abdominal obesity (waist circumference)"
                    },
                    {
                      "value": "Physical activity",
                      "label": "Physical activity"
                    },
                    {
                      "value": "Family history",
                      "label": "Family history"
                    }
                  ],
                  "required": true
                },
                {
                  "title": "Criteria Valid",
                  "type": "checkbox" as const,
                  "required": true
                },
                {
                  "title": "Score to be Given",
                  "type": "number" as const,
                  "required": true
                },
                {
                  "title": "Score Given",
                  "type": "dropdown" as const,
                  "values": [
                    {
                      "value": "0",
                      "label": "0"
                    },
                    {
                      "value": "10",
                      "label": "10"
                    },
                    {
                      "value": "20",
                      "label": "20"
                    },
                    {
                      "value": "30",
                      "label": "30"
                    }
                  ],
                  "required": true
                }
              ],
              "required": true
            }
          ]
        }
      ]
    }
  ]
};

@Injectable({
  providedIn: 'root'
})
export class FoldersDataService {

  constructor(private http: HttpClient) { }

  /**
   * Get all folders data
   */
  getFoldersData(): Observable<FoldersData> {
    return of(foldersData);
  }

  /**
   * Get a specific folder by name
   */
  getFolderByName(folderName: string): Observable<Folder | undefined> {
    return new Observable(observer => {
      this.getFoldersData().subscribe(data => {
        const folder = data.folders.find(f => f.name === folderName);
        observer.next(folder);
        observer.complete();
      });
    });
  }

  /**
   * Get a specific form by folder name and form route
   */
  getFormByRoute(folderName: string, formRoute: string): Observable<Form | undefined> {
    return new Observable(observer => {
      this.getFolderByName(folderName).subscribe(folder => {
        if (folder) {
          const form = folder.forms.find(f => f.route === formRoute);
          observer.next(form);
        } else {
          observer.next(undefined);
        }
        observer.complete();
      });
    });
  }

  /**
   * Get all forms for a specific folder
   */
  getFormsByFolder(folderName: string): Observable<Form[]> {
    return new Observable(observer => {
      this.getFolderByName(folderName).subscribe(folder => {
        if (folder) {
          observer.next(folder.forms);
        } else {
          observer.next([]);
        }
        observer.complete();
      });
    });
  }

  /**
   * Get field types for form generation
   */
  getFieldTypes(): string[] {
    return [
      'radio-button',
      'dropdown', 
      'date',
      'time',
      'number',
      'textarea',
      'checkbox-group',
      'calculated',
      'compound',
      'signature'
    ];
  }

  /**
   * Validate field configuration
   */
  validateField(field: Field): boolean {
    if (!field.title || !field.type) {
      return false;
    }

    // Validate required fields based on type
    switch (field.type) {
      case 'radio-button':
      case 'dropdown':
      case 'checkbox-group':
        return !!(field.values && field.values.length > 0);
      case 'compound':
        return !!(field.fields && field.fields.length > 0);
      default:
        return true;
    }
  }

  /**
   * Generate form controls configuration
   */
  generateFormControls(form: Form): any {
    const controls: any = {};
    
    form.fields.forEach(field => {
      switch (field.type) {
        case 'radio-button':
        case 'dropdown':
          controls[field.title.replace(/\s+/g, '_').toLowerCase()] = [''];
          break;
        case 'checkbox-group':
          controls[field.title.replace(/\s+/g, '_').toLowerCase()] = [[]];
          break;
        case 'number':
          controls[field.title.replace(/\s+/g, '_').toLowerCase()] = [null];
          break;
        case 'compound':
          field.fields?.forEach(subField => {
            controls[subField.title.replace(/\s+/g, '_').toLowerCase()] = [null];
          });
          break;
        default:
          controls[field.title.replace(/\s+/g, '_').toLowerCase()] = [''];
      }
    });

    return controls;
  }

  /**
   * Get field validation rules
   */
  getFieldValidation(field: Field): any[] {
    const validations: any[] = [];
    
    if (field.required) {
      validations.push('required');
    }

    switch (field.type) {
      case 'number':
        validations.push('numeric');
        break;
      case 'date':
        validations.push('date');
        break;
      case 'time':
        validations.push('time');
        break;
    }

    return validations;
  }

  /**
   * Export folders data as JSON
   */
  exportFoldersDataAsJSON(): Observable<string> {
    return new Observable(observer => {
      this.getFoldersData().subscribe(data => {
        const jsonString = JSON.stringify(data, null, 2);
        observer.next(jsonString);
        observer.complete();
      });
    });
  }

  /**
   * Export specific folder as JSON
   */
  exportFolderAsJSON(folderName: string): Observable<string> {
    return new Observable(observer => {
      this.getFolderByName(folderName).subscribe(folder => {
        if (folder) {
          const jsonString = JSON.stringify(folder, null, 2);
          observer.next(jsonString);
        } else {
          observer.error('Folder not found');
        }
        observer.complete();
      });
    });
  }

  /**
   * Export specific form as JSON
   */
  exportFormAsJSON(folderName: string, formRoute: string): Observable<string> {
    return new Observable(observer => {
      this.getFormByRoute(folderName, formRoute).subscribe(form => {
        if (form) {
          const jsonString = JSON.stringify(form, null, 2);
          observer.next(jsonString);
        } else {
          observer.error('Form not found');
        }
        observer.complete();
      });
    });
  }

  /**
   * Get all table fields from all forms
   */
  getAllTableFields(): Observable<any[]> {
    return new Observable(observer => {
      this.getFoldersData().subscribe(data => {
        const tableFields: any[] = [];
        
        data.folders.forEach(folder => {
          folder.forms.forEach(form => {
            form.fields.forEach(field => {
              if (field.type === 'table') {
                tableFields.push({
                  folderName: folder.name,
                  formName: form.name,
                  field: field
                });
              }
            });
          });
        });
        
        observer.next(tableFields);
        observer.complete();
      });
    });
  }

  /**
   * Get table fields by folder
   */
  getTableFieldsByFolder(folderName: string): Observable<any[]> {
    return new Observable(observer => {
      this.getFolderByName(folderName).subscribe(folder => {
        if (folder) {
          const tableFields: any[] = [];
          
          folder.forms.forEach(form => {
            form.fields.forEach(field => {
              if (field.type === 'table') {
                tableFields.push({
                  formName: form.name,
                  field: field
                });
              }
            });
          });
          
          observer.next(tableFields);
        } else {
          observer.next([]);
        }
        observer.complete();
      });
    });
  }
}